/**
 * SFCC-11906 — Constructor.io browse / search behavioural beacons for the
 * Workers storefront.
 *
 * Background: these `*_load` / `*_click` beacons used to fire from the
 * client-side re-rank hook shipped in SFCC-10690 (`use-plp-hydrated-rerank.ts`),
 * whose `getBrowseResults` / `getSearchResults` calls enqueued the `browse` /
 * `search` beacon as a side effect. That hook was removed in `80f5ae55` when
 * PLP / search moved to anonymous server-side SSR, and the beacons went silent
 * on the Workers storefront (they still fire on SFRA / MRT). Once route-truth
 * (SFCC-11879) moved catalog traffic onto the Workers app, Constructor stopped
 * receiving them.
 *
 * This module re-fires the beacons directly via the SDK tracker — no extra CIO
 * round-trip — using the `result_id` + hits the server loader already returns.
 *
 * The CIO client + `window.cnstrcUserId` + beacon bundle are initialised in
 * `root.tsx` / `DataLayerClientPushes`; `useCioClient()` returns the shared
 * singleton (null during SSR / before hydration — every path here null-guards).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { useCioClient } from "~/utils/constructor";

import { cioSearchTerm } from "./cio-sdk-tracking";

/** CIO section for the product catalog. Matches `applyListingParams` server-side. */
const SECTION = "Products";
/** CIO browse filter used for a category listing — mirrors the server
 * `getBrowseResults("group_id", categoryId)` call the re-rank hook used. */
const BROWSE_FILTER_NAME = "group_id";

export interface CioBeaconItem {
  itemId?: string;
  itemName?: string;
}

/**
 * Context published by the PLP / search grid so a memoised product tile can
 * fire `browse_result_click` / `search_result_click` on tile click without the
 * grid drilling per-tile callbacks (a changing callback prop would be dropped
 * by `PlpProductTile`'s `memo`; context bypasses `memo` and always reads fresh).
 * `null` outside a CIO listing (e.g. a tile rendered in quick view).
 */
export interface CioListingBeaconValue {
  kind: "browse" | "search";
  /** Search term; "" for a category browse. */
  term: string;
  /** Category id (CIO group_id filter value); "" for search. */
  categoryId: string;
  resultId?: string;
  resultCount?: number;
  /** 1-based page of the loaded result set. */
  resultPage?: number;
  numResultsPerPage?: number;
}

export const CioListingBeaconContext =
  createContext<CioListingBeaconValue | null>(null);

/**
 * Runs `send` inside a try/catch — CIO beacons are best-effort and an analytics
 * failure must never surface to the shopper. Shared by every beacon in this
 * module (and the PDP `item_detail_load` tracker).
 */
export function safeFireCioBeacon(send: () => void): void {
  try {
    send();
  } catch (err) {
    console.warn("[cio-beacon] send failed:", err);
  }
}

/**
 * Fires `send` once per distinct non-null `key`, re-firing only when the key
 * changes. Encapsulates the ref-guard that collapses React StrictMode's
 * double-invoke and same-value re-renders into a single beacon, plus the
 * best-effort try/catch. Pass `key = null` to suppress (e.g. no client yet,
 * empty result set). Shared by the results-loaded beacon and the PDP
 * `item_detail_load` tracker.
 */
export function useCioBeaconOnce(key: string | null, send: () => void): void {
  const firedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!key || firedKey.current === key) return;
    firedKey.current = key;
    safeFireCioBeacon(send);
    // `send` closes over current values; `key` is the intended re-fire trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * Fires the `browse_result_load` / `search_result_load` beacon once per loaded
 * result set, re-firing when the set changes (filter / sort / query / page →
 * new `result_id`). Suppressed until the client hydrates / with no items.
 */
export function useTrackCioResultsLoaded(
  params: CioListingBeaconValue & { items: CioBeaconItem[] },
): void {
  const cio = useCioClient();
  const { kind, term, categoryId, resultId, resultCount, resultPage, items } =
    params;

  // `result_id` is unique per CIO response, so it alone distinguishes result
  // sets; the rest of the key is belt-and-braces for a missing/empty id.
  const key =
    cio && items.length
      ? `${kind}|${term}|${categoryId}|${resultId ?? ""}|${resultPage ?? ""}`
      : null;

  useCioBeaconOnce(key, () => {
    if (!cio) return;
    // SFCC-11961 conversion search-term rules: a search results load stores
    // the term, a browse load clears it.
    if (kind === "search") cioSearchTerm.store(term);
    else cioSearchTerm.clear();
    const url = window.location.href;
    if (kind === "search") {
      cio.tracker.trackSearchResultsLoaded(term, {
        url,
        items,
        section: SECTION,
        resultCount,
        resultPage,
        resultId,
      });
    } else {
      cio.tracker.trackBrowseResultsLoaded({
        url,
        filterName: BROWSE_FILTER_NAME,
        filterValue: categoryId,
        items,
        section: SECTION,
        resultCount,
        resultPage,
        resultId,
      });
    }
  });
}

/**
 * Returns a stable handler that fires `browse_result_click` /
 * `search_result_click` for a tile. Reads the listing context (fresh, `memo`-
 * safe); no-ops outside a CIO listing or before the client hydrates.
 */
export function useTrackCioResultClick(): (
  item: CioBeaconItem & { position?: number },
) => void {
  const cio = useCioClient();
  const ctx = useContext(CioListingBeaconContext);

  return useCallback(
    ({ itemId, itemName, position }) => {
      // Narrow to a const before the nested closure — a guard on `item.itemId`
      // wouldn't flow into the `safeFireCioBeacon` callback.
      if (!cio || !ctx || !itemId) return;
      safeFireCioBeacon(() => {
        if (ctx.kind === "search") {
          cio.tracker.trackSearchResultClick(ctx.term, {
            itemId,
            itemName,
            resultId: ctx.resultId,
            section: SECTION,
          });
        } else {
          cio.tracker.trackBrowseResultClick({
            filterName: BROWSE_FILTER_NAME,
            filterValue: ctx.categoryId,
            itemId,
            itemName,
            resultId: ctx.resultId,
            resultCount: ctx.resultCount,
            resultPositionOnPage: position,
            numResultsPerPage: ctx.numResultsPerPage,
            section: SECTION,
          });
        }
      });
    },
    [cio, ctx],
  );
}
